import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { BudgetResponse, BudgetStatus, SettingsService } from '../../services/settings.service';

const POLL_MS = 5 * 60_000;
const DISMISS_KEY = 'cc.budget-banner.dismissed-for-day';

/**
 * Daily-spend banner. Polls /api/usage/budget every 5 minutes (and on mount),
 * renders a yellow banner at 75-99% of the cap and a red banner at 100%+. Soft
 * cap only — the user decides what to do; nothing is throttled.
 *
 * Dismissal: clicking the close button suppresses the banner for the rest of
 * the UTC calendar day. We key the dismissal on the response's `date` field
 * (so a fresh day naturally re-arms the banner without page reload).
 */
@Component({
  selector: 'app-budget-banner',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './budget-banner.component.html',
  styleUrls: ['./budget-banner.component.scss'],
})
export class BudgetBannerComponent implements OnInit, OnDestroy {
  private readonly settings = inject(SettingsService);

  protected readonly budget = signal<BudgetResponse | null>(null);
  protected readonly dismissed = signal(false);

  protected readonly visible = computed(() => {
    const b = this.budget();
    if (!b) return false;
    if (this.dismissed()) return false;
    return b.status !== 'ok';
  });

  protected readonly tone = computed<BudgetStatus>(() => this.budget()?.status ?? 'ok');

  private timer?: ReturnType<typeof setInterval>;

  async ngOnInit(): Promise<void> {
    await this.refresh();
    this.timer = setInterval(() => { void this.refresh(); }, POLL_MS);
  }

  ngOnDestroy(): void {
    if (this.timer) clearInterval(this.timer);
  }

  protected dismiss(): void {
    const b = this.budget();
    if (!b) return;
    try { globalThis.localStorage?.setItem(DISMISS_KEY, b.date); }
    catch { /* in-memory fallback below */ }
    this.dismissed.set(true);
  }

  private async refresh(): Promise<void> {
    try {
      const b = await this.settings.budget();
      this.budget.set(b);
      // Re-arm dismissal across UTC days: if the stored dismissal is for a
      // different date than the current response, treat the banner as undismissed.
      let stored: string | null = null;
      try { stored = globalThis.localStorage?.getItem(DISMISS_KEY) ?? null; }
      catch { /* ignore */ }
      this.dismissed.set(stored === b.date);
    } catch {
      // Silent — the banner is supportive, not load-bearing. A failed poll
      // just keeps the previous value (or none) on screen.
    }
  }
}
